import { Stack } from "@mui/material";
import { useParams } from "react-router-dom";
import {
  useGetAccountPlanQuery,
  useGetCampaignDetailsQuery,
  useGetCampaignSummaryQuery,
} from "../api";
import { AppRoutes } from "../types/enums";
import { NavigationBreadcrumbs } from "../components/ui";
import {
  ContentBody,
  ContentHeader,
  NavigationBlock,
} from "../components/company/plan";
import { useEffect, useState } from "react";
import { ToTopBtn } from "../components/ui/buttons";
import { CustomLoader } from "../components/ui/CustomLoader";
import { useNotFoundRedirect } from "../hooks";

const AccountPlanPage = ({ isPublic }: { isPublic?: boolean }) => {
  const { account_id, campaign_id } = useParams();
  const [showScrollTop, setShowScrollTop] = useState<boolean>(false);

  const { data: accountPlan, isLoading: isPlanLoading } =
    useGetAccountPlanQuery(account_id!, {
      skip: !account_id,
      refetchOnMountOrArgChange: true,
    });

  const { data: summary } = useGetCampaignSummaryQuery(
    {
      campaign_id: campaign_id!,
    },
    { skip: isPublic },
  );

  const { data, error, isFetching } = useGetCampaignDetailsQuery(
    { account_id: account_id! },
    { skip: !account_id || isPublic, refetchOnMountOrArgChange: true },
  );

  useNotFoundRedirect({ error });

  useEffect(() => {
    const el = document.body;
    if (el) {
      const handleScroll = () => {
        const { top } = el.getBoundingClientRect();
        setShowScrollTop(top <= -54);
      };

      window.addEventListener("scroll", handleScroll);

      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const isDemo = !!summary?.is_demo;

  if (isPlanLoading || isFetching || !accountPlan) {
    return <CustomLoader />;
  }

  const breadcrumbs = [
    {
      title: data?.product_name || "",
      path: `${AppRoutes.CAMPAINGS}/${campaign_id}`,
    },
    {
      title: "Accounts",
      path: `${AppRoutes.ACCOUNTS_LIST}/${campaign_id}`,
    },
    {
      title: data?.output.company.name || "",
    },
  ];

  return (
    <Stack
      sx={{
        px: { sm: 8, md: 8, lg: 10, xl: 20 },
        pt: 4,
        minHeight: `Calc(100vh - 74px)`,
      }}
      gap="40px"
    >
      <Stack gap="24px">
        {!isPublic && <NavigationBreadcrumbs breadcrumbs={breadcrumbs} />}

        <Stack flexDirection="row" gap="40px" position="sticky">
          <NavigationBlock
            accountPlan={accountPlan}
            isDemo={isDemo}
            isPublic={isPublic}
          />
          <Stack gap="32px" flexGrow={1} paddingBottom="24px">
            <ContentHeader
              isFixed={showScrollTop}
              companyName={data?.output.company.name || ""}
              campaign_id={campaign_id!}
              account_id={account_id!}
              isDemo={isDemo}
              accountPlan={accountPlan}
              isPublic={isPublic}
            />
            <ContentBody accountPlan={accountPlan} />
          </Stack>
        </Stack>
      </Stack>
      {showScrollTop && <ToTopBtn />}
    </Stack>
  );
};

export default AccountPlanPage;
